import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { API } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, MagnifyingGlass, X, Package } from "@phosphor-icons/react";
import MasterExcelToolbar from "../components/MasterExcelToolbar";

export default function BoughtOutMaster() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [makes, setMakes] = useState([]);
  const [search, setSearch] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Form state
  const [form, setForm] = useState({
    description: "", category_id: "", make: "",
    specification: "", hsn_code: "",
  });

  // Category picker
  const [catDropOpen, setCatDropOpen] = useState(false);
  const [catSearch, setCatSearch] = useState("");
  const [newCatInitial, setNewCatInitial] = useState("");
  const catRef = useRef(null);

  // Make picker
  const [makeDropOpen, setMakeDropOpen] = useState(false);
  const makeRef = useRef(null);

  const fetchAll = useCallback(async () => {
    try {
      const [it, cat, mk] = await Promise.all([
        axios.get(`${API}/bought-out`),
        axios.get(`${API}/bought-out/categories`),
        axios.get(`${API}/bought-out/makes`),
      ]);
      setItems(it.data);
      setCategories(cat.data);
      setMakes(mk.data);
    } catch { toast.error("Failed to load Bought Out master"); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Close dropdowns on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (catRef.current && !catRef.current.contains(e.target)) setCatDropOpen(false);
      if (makeRef.current && !makeRef.current.contains(e.target)) setMakeDropOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const selectedCat = useMemo(
    () => categories.find(c => c.id === form.category_id) || null,
    [categories, form.category_id]);

  const filteredCategories = useMemo(() => {
    const s = catSearch.trim().toLowerCase();
    if (!s) return categories;
    return categories.filter(c => (c.name || "").toLowerCase().includes(s));
  }, [categories, catSearch]);

  const filteredMakes = useMemo(() => {
    const s = (form.make || "").trim().toLowerCase();
    if (!s) return makes;
    return makes.filter(m => (m || "").toLowerCase().includes(s));
  }, [makes, form.make]);

  const filteredItems = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return items;
    return items.filter(it =>
      (it.item_code || "").toLowerCase().includes(s) ||
      (it.description || "").toLowerCase().includes(s) ||
      (it.make || "").toLowerCase().includes(s) ||
      (it.category_name || "").toLowerCase().includes(s));
  }, [items, search]);

  const openNew = () => {
    setEditingId(null);
    setForm({ description: "", category_id: "", make: "",
      specification: "", hsn_code: "" });
    setShowForm(true);
  };

  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({
      description: it.description || "",
      category_id: it.category_id || "",
      make: it.make || "",
      specification: it.specification || "",
      hsn_code: it.hsn_code || "",
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ description: "", category_id: "", make: "",
      specification: "", hsn_code: "" });
    setCatSearch("");
    setNewCatInitial("");
  };

  const handleAddCategory = async () => {
    const name = catSearch.trim();
    if (!name) { toast.error("Type a category name first"); return; }
    try {
      const r = await axios.post(`${API}/bought-out/categories`, {
        name, initial: (newCatInitial || name[0]).toUpperCase()
      });
      setCategories(prev => [...prev.filter(c => c.id !== r.data.id), r.data]);
      setForm(f => ({ ...f, category_id: r.data.id }));
      setCatDropOpen(false);
      setCatSearch("");
      setNewCatInitial("");
      toast.success(`Category "${r.data.name}" added`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add category");
    }
  };

  const handleSave = async () => {
    if (!form.description.trim()) { toast.error("Item Description is required"); return; }
    if (!form.category_id) { toast.error("Category is required"); return; }
    if (!form.make.trim()) { toast.error("Make is required"); return; }
    if (!form.specification.trim()) { toast.error("Item Specification is required"); return; }
    try {
      if (editingId) {
        await axios.put(`${API}/bought-out/${editingId}`, form);
        toast.success("Item updated");
      } else {
        const r = await axios.post(`${API}/bought-out`, form);
        toast.success(`Item added — code ${r.data.item_code}`);
      }
      closeForm();
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (it) => {
    if (!window.confirm(`Delete ${it.item_code} — ${it.description}?`)) return;
    try {
      await axios.delete(`${API}/bought-out/${it.id}`);
      toast.success("Deleted");
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <Layout>
      <div className="p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-[#94A3B8]">MASTERS</p>
            <h1 className="text-3xl font-black tracking-tight text-[#0F172A]" style={{fontFamily:'Chivo'}}>
              Bought Out Component Master
            </h1>
            <p className="text-sm text-[#64748B] mt-1">
              Item codes auto-generate as <span className="font-mono">TP-[Category Initial]-[Auto Number]</span>. Same item with different MAKE gets a unique code.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <Input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search by code or description..."
                className="pl-9 min-w-[280px] min-h-[40px]" data-testid="bought-out-search" />
            </div>
            <MasterExcelToolbar basePath="/bought-out" entityLabel="Bought Out Items" onImportDone={fetchAll} />
            <Button onClick={openNew} className="bg-[#1D2951] hover:bg-[#2A3B73] text-white min-h-[40px]"
              data-testid="bought-out-add-btn">
              <Plus size={16} className="mr-1" /> Add Item
            </Button>
          </div>
        </div>

        {/* Form Card */}
        {showForm && (
          <Card className="p-5 border-2 border-[#1D2951]" data-testid="bought-out-form-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-[#1D2951]" style={{fontFamily:'Chivo'}}>
                {editingId ? "Edit Bought Out Item" : "Add Bought Out Item"}
              </h3>
              <Button variant="ghost" size="sm" onClick={closeForm}
                className="min-h-[36px]" data-testid="bought-out-close-form">
                <X size={16} />
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Description */}
              <div>
                <Label className="text-xs font-semibold uppercase text-[#64748B]">Item Description *</Label>
                <Input value={form.description}
                  onChange={e => setForm(f => ({...f, description: e.target.value}))}
                  className="min-h-[40px]" data-testid="bought-out-description-input" />
              </div>

              {/* Category with inline add */}
              <div ref={catRef} className="relative">
                <Label className="text-xs font-semibold uppercase text-[#64748B]">Category *</Label>
                <div className="relative">
                  <Input
                    readOnly
                    placeholder="Select category (use Category Master to add)"
                    value={selectedCat ? `${selectedCat.name} (${selectedCat.initial})` : ""}
                    onClick={() => { setCatDropOpen(!catDropOpen); setCatSearch(""); }}
                    className="min-h-[40px] cursor-pointer" data-testid="bought-out-category-input" />
                </div>
                {catDropOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-lg max-h-[300px] overflow-y-auto">
                    <div className="p-2 border-b border-[#E2E8F0] bg-[#F8FAFC]">
                      <Input autoFocus value={catSearch} onChange={e => setCatSearch(e.target.value)}
                        placeholder="Search or type new category name..."
                        className="min-h-[36px] text-xs" data-testid="bought-out-cat-search" />
                    </div>
                    {filteredCategories.map(c => (
                      <div key={c.id}
                        onClick={() => { setForm(f => ({...f, category_id: c.id})); setCatDropOpen(false); }}
                        className={`px-3 py-2 cursor-pointer hover:bg-[#F5F3FF] text-xs border-b border-[#F1F5F9] flex items-center justify-between ${form.category_id === c.id ? 'bg-[#F5F3FF] font-bold' : ''}`}
                        data-testid={`bought-out-cat-option-${c.name}`}>
                        <span>{c.name}</span>
                        <span className="text-[10px] font-mono font-bold text-[#7C3AED] bg-[#F5F3FF] px-1.5 py-0.5 rounded">
                          {c.initial}
                        </span>
                      </div>
                    ))}
                    {catSearch.trim() && !filteredCategories.some(c => c.name.toLowerCase() === catSearch.trim().toLowerCase()) && (
                      <div className="p-2 border-t border-[#E2E8F0] bg-[#FFFBEB]">
                        <p className="text-[11px] text-[#92400E] mb-1">Add new category:</p>
                        <div className="flex items-center gap-1">
                          <Input value={newCatInitial}
                            onChange={e => setNewCatInitial(e.target.value.slice(0, 3).toUpperCase())}
                            placeholder={catSearch[0]?.toUpperCase() || "I"}
                            className="w-16 min-h-[32px] text-xs font-mono uppercase"
                            data-testid="bought-out-new-cat-initial" />
                          <span className="text-[10px] text-[#64748B]">initial</span>
                          <Button size="sm" onClick={handleAddCategory}
                            className="ml-auto bg-[#10B981] hover:bg-[#059669] text-white min-h-[32px] text-xs"
                            data-testid="bought-out-add-cat-btn">
                            <Plus size={12} className="mr-1" /> Add &quot;{catSearch.trim()}&quot;
                          </Button>
                        </div>
                      </div>
                    )}
                    {filteredCategories.length === 0 && !catSearch.trim() && (
                      <div className="p-3 text-xs text-[#94A3B8] text-center">
                        No categories yet — type a name above to add one.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Make with searchable-and-addable */}
              <div ref={makeRef} className="relative">
                <Label className="text-xs font-semibold uppercase text-[#64748B]">Make (Manufacturer) *</Label>
                <Input value={form.make}
                  onChange={e => { setForm(f => ({...f, make: e.target.value})); setMakeDropOpen(true); }}
                  onFocus={() => setMakeDropOpen(true)}
                  placeholder="Search or add new make"
                  className="min-h-[40px]" data-testid="bought-out-make-input" />
                {makeDropOpen && filteredMakes.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-[#E2E8F0] rounded-lg shadow-lg max-h-[240px] overflow-y-auto">
                    {filteredMakes.map(m => (
                      <div key={m}
                        onClick={() => { setForm(f => ({...f, make: m})); setMakeDropOpen(false); }}
                        className="px-3 py-2 cursor-pointer hover:bg-[#F5F3FF] text-xs border-b border-[#F1F5F9]"
                        data-testid={`bought-out-make-option-${m}`}>
                        {m}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Item Code (readonly, auto) */}
              <div>
                <Label className="text-xs font-semibold uppercase text-[#64748B]">Item Code (Auto-Generated)</Label>
                <Input readOnly value={editingId
                  ? (items.find(i => i.id === editingId)?.item_code || "")
                  : (selectedCat ? `auto: TP-${selectedCat.initial}-{###}` : "auto: TP-{Cat}-{###}")}
                  className="min-h-[40px] font-mono text-[#7C3AED] bg-[#F8FAFC] cursor-not-allowed"
                  data-testid="bought-out-code-display" />
              </div>

              {/* Unit (fixed) */}
              <div>
                <Label className="text-xs font-semibold uppercase text-[#64748B]">Unit</Label>
                <Input readOnly value="Nos"
                  className="min-h-[40px] bg-[#F8FAFC] cursor-not-allowed"
                  data-testid="bought-out-unit-display" />
              </div>

              {/* HSN Code (optional) */}
              <div>
                <Label className="text-xs font-semibold uppercase text-[#64748B]">
                  HSN Code <span className="text-[#94A3B8] font-normal">(optional)</span>
                </Label>
                <Input value={form.hsn_code}
                  onChange={e => setForm(f => ({...f, hsn_code: e.target.value}))}
                  className="min-h-[40px]" data-testid="bought-out-hsn-input" />
              </div>

              {/* Spec (full width) */}
              <div className="md:col-span-2">
                <Label className="text-xs font-semibold uppercase text-[#64748B]">Item Specification *</Label>
                <textarea value={form.specification}
                  onChange={e => setForm(f => ({...f, specification: e.target.value}))}
                  className="w-full min-h-[80px] rounded-md border border-[#E2E8F0] px-3 py-2 text-sm"
                  data-testid="bought-out-spec-input" />
              </div>
            </div>

            <div className="flex items-center gap-2 mt-5 pt-4 border-t border-[#E2E8F0]">
              <Button onClick={handleSave} className="bg-[#1D2951] hover:bg-[#2A3B73] text-white min-h-[40px]"
                data-testid="bought-out-save-btn">
                {editingId ? "Update Item" : "Add Item"}
              </Button>
              <Button variant="outline" onClick={closeForm} className="min-h-[40px]"
                data-testid="bought-out-cancel-btn">
                Cancel
              </Button>
            </div>
          </Card>
        )}

        {/* List */}
        <Card className="border border-[#E2E8F0] overflow-hidden" data-testid="bought-out-list-card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#1D2951] text-white">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Item Code</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Make</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Specification</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">HSN</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Unit</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E2E8F0]">
                {filteredItems.map(it => (
                  <tr key={it.id} className="hover:bg-[#F8FAFC]" data-testid={`bought-out-row-${it.item_code}`}>
                    <td className="px-4 py-3 font-mono font-bold text-[#7C3AED]">{it.item_code}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-[#1D2951]">{it.description}</td>
                    <td className="px-4 py-3 text-sm">{it.category_name}</td>
                    <td className="px-4 py-3 text-sm">{it.make}</td>
                    <td className="px-4 py-3 text-xs text-[#64748B] max-w-[300px] truncate" title={it.specification}>
                      {it.specification}
                    </td>
                    <td className="px-4 py-3 text-xs font-mono">{it.hsn_code || "-"}</td>
                    <td className="px-4 py-3 text-xs">{it.unit || "Nos"}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" onClick={() => openEdit(it)}
                          className="min-h-[32px]" data-testid={`bought-out-edit-${it.item_code}`}>
                          <PencilSimple size={13} />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDelete(it)}
                          className="min-h-[32px] text-[#EF4444] border-[#FECACA] hover:bg-[#FEF2F2]"
                          data-testid={`bought-out-delete-${it.item_code}`}>
                          <Trash size={13} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredItems.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-10 text-sm text-[#94A3B8]">
                      <Package size={32} className="mx-auto mb-2 text-[#CBD5E1]" />
                      No bought-out items yet. Click <b>Add Item</b> to add your first one.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
